/**
 * Multi-view stories (docs/bim-plan/09-controls-views-phasing-addendum.md §6): the
 * FOUR fixed, built-in plan-view recipes — Standard Floor Plan / Dimension Plan /
 * Flooring & Finish Plan / RCP. Hardcoded on purpose: these cover the firm's daily
 * documentation set, and nobody reconfigures what a floor plan or an RCP shows — so
 * there is no view-template configurator, only a stored `planType` token resolved
 * HERE at read time. A future query/filter view mechanism (discussed, out of scope)
 * would be another resolver branch beside `planTypeRecipe` — nothing stored blocks it.
 *
 * Phase filters MULTIPLY across these types (§7 view model): a "Demo Floor Plan" is
 * the 'floor' type plus `view.phase` + `phaseFilter: 'demo'` — never a fifth type.
 * The recipe owns WHAT a view type shows; phasing owns WHEN, independently.
 *
 * "Room labels" from the §6 floor-plan recipe — the long-recorded gap — closed with
 * GL3: rooms exist (the Concept identity model, docs/MODELER.md "GL3: ROOMS") and the
 * recipes carry their derived label layer (`annotations.rooms`) plus the
 * room-separator linework toggle (`annotations.separators`).
 */
import type { CategoryId, LevelId, PlanViewType, ViewRecord } from './types'
import type { DraftingRecord } from './types'
import { listCategories } from './registry'
import { getLevel, listLevels, type ModelDoc } from './doc'
import { OBJECT_KINDS } from './categories/object/objectKinds'
import { OBJECT_FAMILY_CHOICES } from './families'

/** SITE OBJECT FAMILIES (2026-10-19, Matt): the object families that are site content,
 *  not building content — what the site plan shows and the area / dimension plans drop */
/** SITE ITEM LABELS: the composed-override key that says this plan shows the site items
 *  outside the building past their family / category hides (ViewRecipe.showOutdoorItems) */
export const OUTDOOR_ITEMS_KEY = '__outdoorItems'
export const SITE_OBJECT_FAMILIES = ['Planting', 'Site Furnishings', 'Site Utilities', 'Outdoor Cooking']
/** the site CATEGORIES the area and dimension plans drop (plantings ride on 'vine' too) */
const SITE_CATEGORIES: CategoryId[] = ['fence', 'paving', 'pool', 'vine']

/** STRUCTURAL PLANS: the categories and object families a Framing / Foundation Plan drops */
const STRUCTURAL_HIDE: CategoryId[] = ['ceiling', 'roof', 'awning', 'cupola', 'downspout', 'fixture', ...SITE_CATEGORIES]
const STRUCTURAL_HIDE_FAMILIES = [...SITE_OBJECT_FAMILIES, 'Furniture', 'Entourage']
/** every family an object can resolve to (kind defaults + the editable choices) */
function objectFamilyNames(): string[] {
  const set = new Set<string>(['Specialty Equipment'])
  for (const k of OBJECT_KINDS) set.add(k.family)
  for (const f of OBJECT_FAMILY_CHOICES) if (f) set.add(f)
  return [...set]
}


export interface PlanTypeInfo {
  id: PlanViewType
  label: string
  /** 2–3 letter badge for navigator rows / cards */
  short: string
  description: string
}

/** Ordered for pickers — the daily-use order. */
export const PLAN_VIEW_TYPES: PlanTypeInfo[] = [
  {
    id: 'floor', label: 'Floor Plan', short: 'FP',
    description: 'Walls, doors, windows, stairs, casework, railings with door/window tags — no dimension strings.',
  },
  // PRESENTATION PLAN (2026-10-19, Matt: "a new floor plan view type that is meant for
  // presentations to the client. This makes the walls read as a clean poche and all tags
  // and 2D stud/insulation elements are not shown"): the floor plan's geometry with every
  // wall one solid poché, no layer lines, no key studs, no insulation, and no tags.
  {
    id: 'presentation', label: 'Presentation Plan', short: 'PRS',
    description: 'For the client: walls as one clean solid poché, no layer lines, studs, insulation, tags or technical annotation.',
  },
  {
    id: 'dimension', label: 'Dimension Plan', short: 'DIM',
    description: 'Same geometry, lightened so dimension strings and structural grids read clearly.',
  },
  {
    id: 'finish', label: 'Finish Plan', short: 'FIN',
    description: 'Floor boundaries primary, walls thin outline, everything else minimized — flooring and finish layout.',
  },
  {
    id: 'rcp', label: 'Reflected Ceiling Plan', short: 'RCP',
    description: 'Looks UP from the cut plane — ceilings, soffits and overhead structure; floor-level content omitted.',
  },
  // ASBUILT round (Matt: "a new plan type called As-built and have it very
  // limited as to what it shows... merely to show what already exists as a
  // starting point"): a bare geometry trace — no tags, no derived annotation,
  // no user drafting. A per-story type (not level-free like site/roof) since
  // it documents one story's existing shell, the same footprint a Floor Plan
  // would cut. Deliberately NOT the same thing as `phaseFilter: 'existing'`
  // (a display preset any recipe can carry) — this is a distinct, minimal
  // RECIPE, same kind of difference Finish Plan has from Floor Plan.
  {
    id: 'asbuilt', label: 'As-Built Plan', short: 'AB',
    description: 'A bare existing-conditions trace — walls, doors, windows, stairs only, no tags or derived annotation. A starting point for new documentation, not a deliverable on its own.',
  },
  // AREA PLAN (2026-09-29, Matt: "I only want [area breakouts] to show on a new plan
  // type called Area Plan… don't show any tags other than the area breakout shapes"):
  // the plan checker's area document — the model muted underneath, the breakout the
  // only derived annotation. The ONE recipe whose annotations turn areaBreakout on.
  // SUB-FLOOR (bim-plan/28 round 11, the firm's A100 checklist): the foundation read of the
  // lowest story — stem walls, slab-edge and pad footings dashed under the walls, the overall
  // and grid dimensions only, the slab tagged FLR:, finish floor spot elevations, callouts on.
  {
    id: 'subfloor', label: 'Sub-Floor Plan', short: 'SF',
    description: 'The foundation plan: stem walls and footings dashed, overall and grid dimensions only, the slab tagged FLR:, finish floor spots. Looks 6′ below the floor by default so the footings are in range.',
  },
  // STRUCTURAL PLANS (2026-10-21, Matt: "a new floor plan type for levels called Framing Plan
  // … one called Foundation Plan … filtered appropriately per standard drafting conventions
  // for structural drawings"), modeled on the structural engineer's own sheets (ESI/FME —
  // the architecture screened grey, the structure black on top, members called out).
  {
    id: 'framing', label: 'Framing Plan', short: 'FR',
    description: 'Structural framing of the floor this story stands on, looking down: the plan screened with the story below ghosted, bearing walls and posts black, joist spans with size and spacing, beams and headers called out.',
  },
  {
    id: 'foundation', label: 'Foundation Plan', short: 'FDN',
    description: 'Structural foundation: the plan screened, foundation walls black, footings dashed and called out by size, slabs labeled, posts and any raised-floor framing called out.',
  },
  {
    id: 'area', label: 'Area Plan', short: 'AR',
    description: 'The area breakout for plan check — each derived shape crossed, labeled and dimensioned in decimal feet over a muted plan. No tags or other annotation.',
  },
  // docs/bim-plan/14 slice A — the two LEVEL-FREE types: whole-model top-down
  // documents, not bound to any story. Cut anchors at an absolute elevation
  // (default derived above all content), view range below is unlimited.
  {
    id: 'site', label: 'Site Plan', short: 'ST',
    description: 'Whole-model top-down view, not bound to a story — building, terrain and site context together. Cut defaults above all content; unlimited view range.',
  },
  {
    id: 'roof', label: 'Roof Plan', short: 'RF',
    description: 'Whole-model top-down view of the roofs, not bound to a story. Cut defaults above all content; unlimited view range.',
  },
  // STORY POLES (2026-10-03, Matt): the staking plan for the staking company and the
  // jurisdiction — a toned roof plan with the derived poles, eave arms, ribbons, pole
  // tags and field dimensions over it. Level-free like the roof plan it is built on.
  {
    id: 'storypole', label: 'Story Pole Plan', short: 'SP',
    description: 'The staking plan: a toned roof plan with every story pole, eave arm and ribbon derived from the model, tagged with its top elevation and dimensioned to the property lines.',
  },
]

/**
 * PLANTING PLAN (2026-10-20): a site plan with `plantings` set — see ViewRecord.plantings.
 * The UI-level entry the New Plan View dialog offers beside Site Plan (the Demolition entry's
 * precedent: a compound entry, never a kernel type).
 */
export const PLANTING_PLAN_ENTRY = {
  id: 'planting', label: 'Planting Plan', short: 'PLT',
  description: 'A site plan that shows the plantings, trees and vines — every other plan, the site plan included, leaves them off.',
} as const
export function isPlantingPlan(view: Pick<ViewRecord, 'scope' | 'plantings'> | null | undefined): boolean {
  return !!view && view.scope.kind === 'plan' && view.scope.planType === 'site' && view.plantings === true
}

/**
 * ROOF FRAMING PLAN (2026-10-21): a roof plan with `roofFraming` set — see
 * ViewRecord.roofFraming. The Roof Plan category's compound entry (the Planting Plan's
 * precedent): the structural framing of the roofs, the story Framing Plans only ever look down.
 */
export const ROOF_FRAMING_PLAN_ENTRY = {
  id: 'roofframing', label: 'Roof Framing Plan', short: 'RFR',
  description: 'The roof framing: the roofs screened, rafter spans with size and spacing, ridges, hips and valleys. The story Framing Plans show the floor framing; this shows the roofs.',
} as const
export function isRoofFramingPlan(view: Pick<ViewRecord, 'scope' | 'roofFraming'> | null | undefined): boolean {
  return !!view && view.scope.kind === 'plan' && view.scope.planType === 'roof' && view.roofFraming === true
}

export const planTypeInfo = (t: PlanViewType): PlanTypeInfo =>
  PLAN_VIEW_TYPES.find((p) => p.id === t) ?? PLAN_VIEW_TYPES[0]

/**
 * docs/bim-plan/14 slice A: the LEVEL-FREE plan types — views of these types omit
 * `levelId` from their plan scope entirely (the invariant createPlanView enforces).
 * The one predicate every "does this plan belong to a story?" branch consults.
 */
export const isLevelFreePlanType = (t: PlanViewType | undefined | null): boolean =>
  t === 'site' || t === 'roof' || t === 'storypole'

/** scope-level twin of isLevelFreePlanType — true for a plan scope with no story */
export const isLevelFreePlanScope = (scope: ViewRecord['scope'] | null | undefined): boolean =>
  !!scope && scope.kind === 'plan' && !scope.levelId

/**
 * LIVE-VIEW HARDENING (2026-08-21): true for a view that IS a "live" home surface —
 * a story's implicit untyped plan, or a level-free category's designated `live` view
 * (`ensureCategoryLivePlan` in commands/views.ts) — and false for every other view,
 * including every non-plan kind. The one predicate every "can this go on a sheet /
 * be renamed / change type?" guard consults, so the two liveness mechanisms (absent
 * `planType` for stories, stored `live: true` for site/roof) never have to be
 * re-derived at each call site.
 */
export function isLiveOnlyView(view: Pick<ViewRecord, 'scope'> | null | undefined): boolean {
  if (!view?.scope || view.scope.kind !== 'plan') return false
  const scope = view.scope
  return scope.levelId ? !scope.planType : !!scope.live
}

/**
 * The fixed recipe behind a type. Everything here compiles into the EXISTING view
 * machinery at read time — `hide`/`halftone` become category overrides
 * (composedOverrides), `fillHide` drops poché without touching linework,
 * `wallsOutline`/`floorsPrimary` are the two linework transforms overrides can't
 * express (lineworkStyle.js#applyRecipeLinework), `lookUp` flips the cut direction
 * in the geometry worker, and `annotations`/`drafting` gate the derived + user
 * annotation layers.
 */
export interface PlanTypeRecipe {
  /** RCP: project UP from the cut plane instead of down (plan.ts) */
  lookUp: boolean
  hide: CategoryId[]
  halftone: CategoryId[]
  /** halftone EVERY category (dimension plan) — hide/halftone lists still apply */
  muteModel: boolean
  /** walls read as a thin outline: layer lines dropped, cut weight capped */
  wallsOutline: boolean
  /** floor boundaries promoted from dashed-projected to solid primary linework */
  floorsPrimary: boolean
  /** PRESENTATION PLAN: every cut wall reads as ONE solid poché — the per-layer fills,
   *  layer lines, key studs and insulation drop (worker side: PlanCutRequest.wallsPoche) */
  wallsPoche?: boolean
  /** categories whose poché FILLS drop while their linework stays */
  fillHide: CategoryId[]
  /** SUB-FLOOR (bim-plan/28 round 11): stem walls and footings draw DASHED (as below the floor)
   *  even where the cut passes through them — the firm's sub-floor convention */
  foundationsDashed?: boolean
  /** STRUCTURAL PLANS: footings (only) draw dashed — the engineer's foundation plan */
  footingsDashed?: boolean
  /** STRUCTURAL PLANS: the plan reads as the engineer's background — every category but
   *  these screened, walls as plain double lines (no layers, studs or poché), and only the
   *  STRUCTURAL walls (exterior, concrete, or carrying a framing line) black; the worker
   *  marks those (PlanCutRequest.structural) */
  structural?: boolean
  /** categories a structural plan keeps black (everything else screened) */
  screenExcept?: CategoryId[]
  /** when set, the ONLY object families this plan type shows (every other object
   *  family hidden) — the site plan shows Planting, not furniture (2026-10-05) */
  objectFamilies?: string[]
  /** object families this plan type hides outright (the area and dimension plans
   *  drop the site families, 2026-10-19) — composed like the category hides */
  hideFamilies?: string[]
  /** SITE ITEM LABELS (2026-10-20, Matt: "The site plans … are not showing the AC condensers
   *  when it should"): the labeled site items (kernel siteItemLabels.ts — condensers, exterior
   *  panels and meters, pool equipment …) that sit OUTSIDE the building's walls show on this
   *  plan whatever their family or category hide says; inside ones stay hidden. Composed as
   *  the `OUTDOOR_ITEMS_KEY` override, which the plan's applyOverrides reads off each path's
   *  `outdoor` mark (bim-geometry siteOutdoor.ts) */
  showOutdoorItems?: boolean
  /** derived-annotation defaults — view.annotations overrides key-wise.
   *  Phase-B key split (docs/bim-plan/10 §3.6 Q5): `grids` = structural grid lines,
   *  `datums` = level datum lines. One stored key used to gate both under the name
   *  `datums`, with different content per surface — now each is honestly named. */
  annotations: { tags: boolean; markers: boolean; datums: boolean; grids: boolean; rooms: boolean; separators: boolean
    /** STAIR round (audit item 6): derived per-tread numbers + the UP arrow on
     *  every stair — never hand-placed; documentation defaults follow the plan type */
    stairNotes: boolean
    /** bim-plan/14 slice C: site records (property line / setbacks / easements) —
     *  full linetypes + metes-and-bounds on 'site' plans, screened ghost elsewhere */
    site: boolean
    /** bim-plan/14 slice D — the ROOF ANNOTATION package, all derived from the roof
     *  skeleton (never hand-placed): RIDGE/HIP/VALLEY edge tags, per-face slope
     *  arrows with rise:12, and real-world elevation callouts (ridges/eaves/
     *  parapets). Default ON only in the roof recipe; toggleable per view. */
    roofTags: boolean; slopeArrows: boolean; roofElevs: boolean
    /** slice E: the tributary-drainage overlay (strip boundaries + gutter/downspout
     *  sizing callouts) — a WORKING diagram, default OFF even on the roof plan */
    drainage: boolean
    /** ROOF HATCH round (Matt: "surface hatch patterns... shown partially on a
     *  plane, at the corners... breakline"): each roof face's topMaterial cut
     *  pattern drawn in one or two corner wedges (roofHatch.js), never the whole
     *  face. Default ON only in the roof recipe, same family as roofTags/
     *  slopeArrows/roofElevs above. */
    roofHatch: boolean
    /** AREA PLAN: the level's area breakout — ON only in the area recipe; absent = off */
    areaBreakout?: boolean
    /** AUTO-DIMENSIONS (2026-09-29): the derived dimension strings (grids, exterior faces
     *  of stud, opening centerlines, strings along each gridline) — ON only in the
     *  dimension recipe; absent = off */
    autoDims?: boolean
    /** STORY POLES (2026-10-03): the derived stake set — ON only in the storypole
     *  recipe; absent = off */
    storyPoles?: boolean
    /** STRUCTURAL PLANS (2026-10-21): framing callouts — ON only on Framing / Foundation */
    framing?: boolean
    /** STRUCTURAL PLANS: footing + slab callouts — ON only on Foundation Plans */
    footings?: boolean }
  /** which user drafting kinds render in this view type */
  drafting: Record<DraftingRecord['kind'], boolean>
}

const RECIPES: Record<PlanViewType, PlanTypeRecipe> = {
  // the presentation plan: model reads normally, overhead noise (ceilings) off,
  // tags on, NO dimension strings — those belong to the dimension plan.
  // PLAN-RANGE TAGS follow-up (2026-08-26, Matt's test-pass check 3 — he expected
  // in-range roofs VISIBLE on Floor Plans, not silently transparent): 'roof'
  // dropped from this hide too, same as dimension/asbuilt — the doctrine "if an
  // item is in range of the view settings, it shows, and if it's a solid object,
  // it occludes" now holds on every look-down story recipe. A per-view override
  // can still hide roofs, and (via hiddenCategories reaching the occluder
  // substrate) a hidden roof then genuinely stops occluding as well.
  floor: {
    lookUp: false,
    hide: ['ceiling'],
    halftone: [],
    muteModel: false, wallsOutline: false, floorsPrimary: false,
    fillHide: [],
    annotations: { tags: true, markers: true, datums: true, grids: true, rooms: true, separators: true, site: true, stairNotes: true, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false },
    drafting: { dimension: false, text: true, spot: true, line: true, region: true },
  },
  // PRESENTATION PLAN: the floor plan for the client. Walls one solid poché (the
  // worker draws it — wallsPoche), no tags of any kind (door/window/wall, rooms, stair
  // notes, keynotes), no markers, grids or datums, no dimensions or spot elevations.
  // Text, lines and regions stay so a designer can still add a label. Every annotation
  // key can still be switched back on per view.
  presentation: {
    lookUp: false,
    hide: ['ceiling'],
    halftone: [],
    muteModel: false, wallsOutline: false, floorsPrimary: false, wallsPoche: true,
    fillHide: [],
    annotations: { tags: false, markers: false, datums: false, grids: false, rooms: false, separators: false, site: false, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false },
    drafting: { dimension: false, text: true, spot: false, line: true, region: true },
  },
  // same geometry, suppressed so the dimension strings and grids are the subject.
  // PLAN-RANGE TAGS round (2026-08-25, Matt: "Dimension type plans do not show
  // the outlines of roof in the view... Those roof outlines should show. They
  // already occlude like they are supposed to"): 'roof' dropped from `hide` —
  // an in-range roof draws its outlines on a Dimension Plan (muted like
  // everything else) AND keeps occluding, per the doctrine "if an item is in
  // range of the view settings, it shows, and if it's a solid object, it
  // occludes." The roof annotation package (tags/slopes/elevs/hatch) stays off.
  // Floor/Finish keep their roof hides — those recipes' "overhead noise"
  // framing stands, and with hiddenCategories now reaching the plan cut's
  // occluder substrate, a hidden roof no longer occludes there either.
  dimension: {
    lookUp: false,
    // no site features or plantings (2026-10-19, Matt)
    hide: ['ceiling', ...SITE_CATEGORIES],
    hideFamilies: SITE_OBJECT_FAMILIES,
    halftone: [],
    muteModel: true, wallsOutline: false, floorsPrimary: false,
    fillHide: [],
    annotations: { tags: false, markers: false, datums: true, grids: true, rooms: true, separators: true, site: true, stairNotes: true, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false, autoDims: true },
    drafting: { dimension: true, text: true, spot: true, line: true, region: true },
  },
  // floor material boundaries primary; walls a thin outline (no layers, no poché);
  // everything else minimized so the flooring layout is the drawing
  finish: {
    lookUp: false,
    // AWNINGS (2026-10-08): an exterior hood is clutter on a finish plan, like the roof
    hide: ['ceiling', 'roof', 'awning', 'cupola'],
    // 'topo' removed 2026-08-24 — now covered unconditionally by composedOverrides'
    // site-plan-only terrain rule, which supersedes a halftone here anyway.
    halftone: ['door', 'window', 'stair', 'ramp', 'railing', 'fence', 'casework', 'object', 'fixture', 'column', 'beam'],
    muteModel: false, wallsOutline: true, floorsPrimary: true,
    fillHide: ['wall', 'column'],
    // rooms stay ON — the finish plan's room basis (GL3: flooring reads per room)
    annotations: { tags: false, markers: false, datums: false, grids: false, rooms: true, separators: true, site: false, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false },
    drafting: { dimension: false, text: true, spot: false, line: true, region: true },
  },
  // look-UP cut: ceilings/soffits/overhead structure solid, floor-level categories
  // omitted. Walls still cut (RCP convention); door/window symbols draw DASHED
  // (2026-11-03) so switches and lights can be laid out against swings.
  rcp: {
    lookUp: true,
    // RCP VISIBILITY round (2026-08-24): 'fixture' dropped from this blanket
    // hide — real electrical-plan convention shows every LIGHT (sconce
    // included) and every SWITCH on the RCP (so a switch can be linked to
    // what it controls), never outlet/lowVoltage. A category-wide hide can't
    // express a per-kind split like that; plan.ts's own fixture-symbol loop
    // does the gating instead (isLightEmitter/isSwitch when lookUp,
    // !isCeilingFixtureKind otherwise), so this hide list no longer touches
    // fixtures at all.
    // 'topo' removed 2026-08-24 — now covered unconditionally by composedOverrides'
    // site-plan-only terrain rule.
    // RCP LAYOUT AIDS (2026-11-03, Matt: "show casework and furniture on the RCP as if we're
    // looking down in faint lines so we can lay out lighting relative to key elements"):
    // casework and objects moved from hide to halftone — the plan cut draws this level's
    // casework from above and the furniture symbols; both snap and take temp dimensions
    // DOORS + WINDOWS ON THE RCP (2026-11-03, Matt: "allow me to see the doors and windows.
    // Dash the panels and swing lines... so we're not screwing up a switch location or bumping
    // into a light"): no longer hidden — plan.ts draws their panels, swings and glazing DASHED
    // on a look-up cut (the frames stay cut where the plane passes through them)
    // 'pool' (2026-11-04, Brandon: "the pool is showing in the RCP") — ground-level site work.
    // STAIRS ON THE RCP (2026-11-04, Brandon: "I also need to see the outline of the stairs"):
    // out of hide, into halftone — the stair's own plan lines read faint, dashed below the cut
    hide: ['ramp', 'railing', 'fence', 'awning', 'cupola', 'paving', 'pool'],
    halftone: ['casework', 'object', 'stair'],
    muteModel: false, wallsOutline: false, floorsPrimary: false,
    fillHide: [],
    // separators are floor-level content — off the RCP. Room labels ON (2026-10-14, Matt: "I
    // want to see room tags also on the Reflected ceiling plan") — name and number, lifted
    // above the ceiling-height tag (annotation/rooms.js RCP_ROOM_LIFT). Markers ON (2026-10-14,
    // Matt: "allow sections to show on reflected ceiling plans") — section cuts only, the
    // marker builders drop elevations and detail callouts on an RCP
    annotations: { tags: false, markers: true, datums: true, grids: true, rooms: true, separators: false, site: false, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false },
    // dimensions ON (2026-11-03, Matt: "show dimensions that we make on the Reflected Ceiling
    // Plans") — a dimension placed on an RCP used to vanish the moment the tool let go
    drafting: { dimension: true, text: true, spot: false, line: true, region: true },
  },
  // ASBUILT round: bare shell only — no tags (Matt's explicit example), no
  // derived annotation of any kind, no user drafting. Overhead/interior-symbol
  // categories off same as Floor Plan; this is intentionally MORE restrictive
  // than every other story-level recipe, since its whole purpose is a clean
  // trace base for new documentation to be built ON, not a finished drawing.
  // PROVISIONAL — first pass, expect field-feedback refinement like every
  // other recipe here.
  // ROOF VISIBILITY fix (Matt: "roofs are not visible... it looks to be
  // related to the As-built type plan settings" — traced through a hidden-
  // element red herring and a View Depth red herring before landing here):
  // 'roof' dropped from `hide` — an as-built trace of EXISTING conditions
  // needs to show existing roofs (a lower/accessory structure can be
  // entirely unrepresented above the cut otherwise, its roof the only trace
  // of it at all), unlike Floor Plan's "overhead noise" framing, which is
  // about a NEW design's roof competing with the walls/rooms it's presenting.
  // 'ceiling' stays hidden — not reported as an issue, and still genuinely
  // overhead-only content for a trace base.
  asbuilt: {
    lookUp: false,
    hide: ['ceiling'],
    halftone: [],
    muteModel: false, wallsOutline: false, floorsPrimary: false,
    fillHide: [],
    annotations: { tags: false, markers: false, datums: false, grids: false, rooms: false, separators: false, site: false, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false },
    // WALL TAG round (Matt: "unhide text and dimensions on as-built type
    // plans"): a drafter needs to add dimension strings and notes to an
    // as-built trace while building it out — the "bare shell, nothing else"
    // stance from the earlier ASBUILT round was too restrictive for that.
    // Still no spot elevations/lines/regions and no derived annotation
    // (tags/markers/grids/rooms) — those stay off by design.
    drafting: { dimension: true, text: true, spot: false, line: false, region: false },
  },
  // AREA PLAN: the model muted as a base, the area breakout the ONLY annotation — no
  // tags, markers, rooms, grids, datums or stair notes. Text notes still render (a
  // title or a note to the checker); every other user drafting kind is off.
  subfloor: {
    lookUp: false,
    hide: ['ceiling', 'roof', 'awning', 'cupola', 'casework', 'object', 'fixture'],
    halftone: [],
    muteModel: false, wallsOutline: false, floorsPrimary: false,
    fillHide: ['stem', 'footing'],
    foundationsDashed: true,
    annotations: { tags: false, markers: true, datums: true, grids: true, rooms: false, separators: false, site: false, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false, autoDims: true },
    drafting: { dimension: true, text: true, spot: true, line: true, region: true },
  },
  // STRUCTURAL PLANS: the engineer's convention (ESI/FME's own S-sheets) — the architecture
  // stays as a screened background (doors, fixtures, stairs all there, grey), the structure
  // reads black over it, and the derived framing callouts are the subject. No door/wall tags,
  // room labels or dimension strings of the architect's; grids and section marks stay.
  // WHAT THE ENGINEER'S BACKGROUND KEEPS (2026-10-21, checked against ESI/FME's sheets and on
  // Santiago): walls, doors, windows, stairs, casework, plumbing fixtures and appliances, screened.
  // Gone: lights, switches and outlets (the fixture category), furniture and cars (the Furniture
  // and Entourage families), site content, ceilings and roofs (the roof framing is drawn as
  // callouts), interior elevation callouts and the architect's old level-wide notes.
  framing: {
    lookUp: false,
    hide: STRUCTURAL_HIDE,
    hideFamilies: STRUCTURAL_HIDE_FAMILIES,
    halftone: [],
    muteModel: false, wallsOutline: false, floorsPrimary: false,
    fillHide: ['wall', 'column', 'stem'],
    structural: true,
    screenExcept: ['wall', 'column', 'beam', 'stem', 'footing'],
    annotations: { tags: false, markers: true, datums: true, grids: true, rooms: false, separators: false, site: false, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false, framing: true },
    drafting: { dimension: true, text: true, spot: true, line: true, region: true },
  },
  foundation: {
    lookUp: false,
    hide: STRUCTURAL_HIDE,
    hideFamilies: STRUCTURAL_HIDE_FAMILIES,
    halftone: [],
    muteModel: false, wallsOutline: false, floorsPrimary: false,
    fillHide: ['wall', 'column', 'stem', 'footing'],
    structural: true,
    footingsDashed: true,
    screenExcept: ['wall', 'column', 'beam', 'stem', 'footing'],
    annotations: { tags: false, markers: true, datums: true, grids: true, rooms: false, separators: false, site: false, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false, framing: true, footings: true },
    drafting: { dimension: true, text: true, spot: true, line: true, region: true },
  },
  area: {
    lookUp: false,
    // no site features or plantings (2026-10-19, Matt)
    hide: ['ceiling', ...SITE_CATEGORIES],
    hideFamilies: SITE_OBJECT_FAMILIES,
    halftone: [],
    muteModel: true, wallsOutline: false, floorsPrimary: false,
    fillHide: [],
    annotations: { tags: false, markers: false, datums: false, grids: false, rooms: false, separators: false, site: false, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false, areaBreakout: true },
    drafting: { dimension: false, text: true, spot: false, line: false, region: false },
  },
  // ---- docs/bim-plan/14 slice A: the two level-free types. PROVISIONAL recipes —
  // slice C adds the site-record annotation keys, slice D the roof annotation
  // package (ridge/hip/valley tags, slope arrows, elevation callouts, drainage);
  // slice B adds top-down occlusion. Until then these are honest "everything in
  // range, minus obvious interior noise" reads.
  // Site plan: the context document — building + terrain + (slice C) site records.
  // Interior symbol categories off (casework/fixtures/objects read through the
  // roof without occlusion and are never site-plan content).
  site: {
    lookUp: false,
    // PLANTINGS ON SITE PLANS (2026-10-05, Matt: "I also want to be able to see
    // plantings in site plans, whereas right now they appear to be filtered out"):
    // objects are no longer hidden as a category — only their Planting family shows
    // (objectFamilies below); furniture, appliances, fixtures etc. stay off
    // SITE FURNISHINGS (2026-10-19, Matt: Santiago's site plan wasn't showing the trash
    // cans): every site family shows — trash carts, fire pits, umbrellas, meters, grills
    hide: ['ceiling', 'casework', 'fixture'],
    objectFamilies: SITE_OBJECT_FAMILIES,
    showOutdoorItems: true,
    halftone: [],
    muteModel: false, wallsOutline: false, floorsPrimary: false,
    fillHide: [],
    // grids default ON here (2026-08-26, Matt's test pass: "I can technically add
    // grids, but they do not show on the site plan... we can set grids relative
    // to site features when locating buildings") — the LEVEL-FREE TOOLSET PARITY
    // round made grids drawable on the site plan, so hiding them by default made
    // the tool look broken. The per-view Grids toggle still turns them off.
    annotations: { tags: false, markers: false, datums: true, grids: true, rooms: false, separators: false, site: true, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false },
    drafting: { dimension: true, text: true, spot: true, line: true, region: true },
  },
  // Roof plan: the drainage/geometry document — topo (now unconditional via
  // composedOverrides' site-plan-only rule, 2026-08-24) and site context OFF
  // by default (Matt's resolved Q8; per-view toggles still available).
  roof: {
    lookUp: false,
    hide: ['ceiling', 'casework', 'fixture', 'object', 'door', 'window'],
    halftone: [],
    muteModel: false, wallsOutline: false, floorsPrimary: false,
    fillHide: [],
    // section/elevation callouts ON (2026-10-19, Matt: "I want to be able to see the section
    // and elevation callouts on the roof plans")
    annotations: { tags: false, markers: true, datums: true, grids: false, rooms: false, separators: false, site: false, stairNotes: false, roofTags: true, slopeArrows: true, roofElevs: true, drainage: false, roofHatch: false },   // roof hatch SHELVED 2026-10-15 (roofHatch.js)
    drafting: { dimension: true, text: true, spot: true, line: true, region: true },
  },
  // STORY POLES: the roof plan's geometry, every category muted (the "lightly toned
  // roof plan" Matt asked for), the property lines + setbacks on, and the stake set the
  // only derived annotation — its own tags carry the elevations, so the roof package's
  // tags/elevations/hatch stay off.
  storypole: {
    lookUp: false,
    hide: ['ceiling', 'casework', 'fixture', 'object', 'door', 'window'],
    halftone: [],
    muteModel: true, wallsOutline: false, floorsPrimary: false,
    fillHide: [],
    annotations: { tags: false, markers: false, datums: false, grids: false, rooms: false, separators: false, site: true, stairNotes: false, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false, storyPoles: true },
    drafting: { dimension: true, text: true, spot: true, line: true, region: true },
  },
}

export const planTypeRecipe = (t: PlanViewType): PlanTypeRecipe => RECIPES[t]

/**
 * DEMOLITION BADGE (2026-08-26, Matt's test pass: "Demo plans listed in the
 * project browser still show 'FP' next to them... I expected 'DEM'"): the ONE
 * badge rule every surface shares. A Demolition Plan IS Floor Plan × the demo
 * phase preset (never a kernel type — the NewPlanViewModal doctrine), but the
 * browser badge should say what the DRAWING is: any typed plan view whose
 * stored phaseFilter is 'demo' reads 'DEM' instead of its recipe's short.
 * Undefined for untyped/non-plan views (callers already show nothing there).
 */
export function planViewBadge(view: Pick<ViewRecord, 'scope' | 'phaseFilter' | 'plantings' | 'roofFraming'>): string | undefined {
  if (view.scope.kind !== 'plan' || !view.scope.planType) return undefined
  if (view.phaseFilter === 'demo') return 'DEM'
  if (isPlantingPlan(view as Pick<ViewRecord, 'scope' | 'plantings'>)) return PLANTING_PLAN_ENTRY.short
  if (isRoofFramingPlan(view as Pick<ViewRecord, 'scope' | 'roofFraming'>)) return ROOF_FRAMING_PLAN_ENTRY.short
  return planTypeInfo(view.scope.planType).short
}

/** The recipe governing a view, or null — untyped plans and every non-plan kind. */
export function viewRecipe(view: Pick<ViewRecord, 'scope'> | null | undefined): PlanTypeRecipe | null {
  if (!view || view.scope.kind !== 'plan' || !view.scope.planType) return null
  return planTypeRecipe(view.scope.planType)
}

type Overrides = ViewRecord['overrides']

/**
 * The category overrides a renderer should actually apply: the recipe compiled to
 * override entries, with the view's own STORED overrides winning key-wise on top.
 * Untyped views return their stored overrides verbatim — zero behavior change.
 */
export function composedOverrides(view: Pick<ViewRecord, 'scope' | 'overrides' | 'plantings' | 'roofFraming'>): Overrides {
  const recipe = viewRecipe(view)
  if (!recipe) return view.overrides ?? {}
  const out: Overrides = {}
  for (const { id } of listCategories()) {
    const e: { visible?: boolean; halftone?: boolean } = {}
    if (recipe.hide.includes(id)) e.visible = false
    if (recipe.muteModel || recipe.halftone.includes(id) || (recipe.screenExcept && !recipe.screenExcept.includes(id))) e.halftone = true
    if (e.visible !== undefined || e.halftone !== undefined) out[id] = e
  }
  // an object-family allow-list (the site plan's Planting) hides every other object
  // family — BEFORE the stored per-view overrides, so a view can still turn one on
  if (recipe.objectFamilies) {
    const keep = new Set(recipe.objectFamilies)
    for (const fam of objectFamilyNames()) if (!keep.has(fam)) (out as Record<string, { visible?: boolean }>)[fam] = { visible: false }
  }
  for (const fam of recipe.hideFamilies ?? []) (out as Record<string, { visible?: boolean }>)[fam] = { visible: false }
  // ROOF FRAMING PLAN: the roofs read as the screened background of their own framing
  // ROOF FRAMING PLAN: the roofs read as the screened background of their own framing; the site
  // (street, pool, paving, decks and patios, stairs, plantings, site furnishings) is not the
  // engineer's — it drops (2026-10-21, checked on Santiago)
  if (isRoofFramingPlan(view)) {
    for (const c of ['roof', 'awning', 'cupola'] as CategoryId[]) out[c] = { ...out[c], halftone: true }
    for (const c of ['floor', 'stair', 'ramp', 'railing', ...SITE_CATEGORIES] as CategoryId[]) out[c] = { ...out[c], visible: false }
    for (const fam of SITE_OBJECT_FAMILIES) (out as Record<string, { visible?: boolean }>)[fam] = { visible: false }
  }
  if (recipe.showOutdoorItems) (out as Record<string, { visible?: boolean }>)[OUTDOOR_ITEMS_KEY] = { visible: true }
  for (const [cat, ov] of Object.entries(view.overrides ?? {})) {
    out[cat as CategoryId] = { ...out[cat as CategoryId], ...ov }
  }
  // TERRAIN IS SITE-PLAN-ONLY (2026-08-24, Matt: "hide the terrain object on
  // every single plan type except the site plan type. Not just visually, but
  // make it truly gone in those views — not highlightable, not selectable"):
  // ONE central rule instead of listing 'topo' in every other recipe's own
  // hide array (the exact "chasing them one by one" risk this round's RCP
  // audit turned up — a future new plan type would silently re-open the gap).
  // Applied AFTER the stored per-view override merge so it's absolute — a
  // per-view manual override can't quietly re-show terrain on, say, a Floor
  // Plan the way it can for every other recipe-hidden category. This is the
  // ONLY consumer that decides drawing (stylePaths/LineworkFills) AND, via
  // the same composedOverrides call in ModelerPage.jsx's surfaceHiddenSet,
  // pick/hover/marquee — one rule change makes terrain genuinely gone in
  // both senses everywhere a typed plan isn't 'site'.
  const scope = view.scope
  const planType = scope.kind === 'plan' ? scope.planType : undefined
  if (planType && planType !== 'site') out.topo = { visible: false }
  // PLANTINGS ARE PLANTING-PLAN-ONLY (2026-10-19, Matt: "No plans except for a specialty site
  // plan meant to show plantings should show plantings"; 2026-10-20: "not visible on site plans
  // by default unless we create a specific 'Planting Plan' type"): the terrain rule's twin —
  // plants and trees (the Planting object family) and painted vines are gone on every typed
  // plan but a Planting Plan (a site plan with `plantings`), absolutely, past any stored
  // per-view override
  if (planType && !isPlantingPlan(view)) {
    ;(out as Record<string, { visible?: boolean }>).Planting = { visible: false }
    out.vine = { visible: false }
  }
  return out
}

/**
 * PLAN-RANGE TAGS round: the composed hidden-category LIST for a view — the
 * `visible === false` entries of composedOverrides, as the flat array the plan
 * cut's `hiddenCategories` request field wants (the vertical views' fetch path
 * derives the same thing from stored overrides; plans compose the recipe in).
 * One derivation so the occluder substrate and the drawing filter can never
 * disagree about what "hidden" means.
 */
export function composedHiddenCategories(view: Pick<ViewRecord, 'scope' | 'overrides' | 'plantings' | 'roofFraming'>): CategoryId[] {
  return Object.entries(composedOverrides(view) ?? {})
    .filter(([, o]) => o?.visible === false)
    .map(([c]) => c as CategoryId)
}

/**
 * The override set for poché FILLS — composedOverrides plus the recipe's fill-only
 * hides (a finish plan keeps the wall outline but drops its poché).
 */
export function composedFillOverrides(view: Pick<ViewRecord, 'scope' | 'overrides' | 'plantings' | 'roofFraming'>): Overrides {
  const recipe = viewRecipe(view)
  const base = composedOverrides(view)
  if (!recipe || !recipe.fillHide.length) return base
  const out: Overrides = { ...base }
  for (const cat of recipe.fillHide) out[cat] = { ...out[cat], visible: false }
  return out
}

/**
 * The one resolver for a view's derived-annotation toggles: recipe defaults (all-on
 * for untyped views — today's exact semantics), overridden key-wise by any explicit
 * `view.annotations` values.
 *
 * `grids` legacy fallback: before the Phase-B key split, one stored `datums` value
 * gated the structural grids too — a record written back then keeps meaning what it
 * meant (a stored `datums: false` still hides grids until an explicit `grids` value
 * is written). No data migration; new writes use the honest key.
 */
export function effectiveAnnotations(
  view: Pick<ViewRecord, 'scope' | 'annotations'> & { phaseFilter?: ViewRecord['phaseFilter']; roofFraming?: boolean },
  /** GL3: `sheet` = resolving for a sheet viewport / PDF — separators DEFAULT off
   *  there ("off on sheets by default", the amendment); an explicit stored value
   *  wins in both contexts. Every other key resolves identically in both. */
  opts?: { sheet?: boolean },
): { tags: boolean; markers: boolean; datums: boolean; grids: boolean; rooms: boolean; separators: boolean; site: boolean; stairNotes: boolean; roofTags: boolean; slopeArrows: boolean; roofElevs: boolean; drainage: boolean; roofHatch: boolean; areaBreakout: boolean; autoDims: boolean; parkingStalls: boolean; storyPoles: boolean; framing: boolean; footings: boolean; demoKeynotes: boolean; workKeynotes: boolean; nonconforming: boolean; demoTabs: boolean; heightDatum: boolean; heightLimit: boolean; buildingMask: boolean; finishFloor: boolean; hookups: boolean; downspouts: boolean; floorPatterns: boolean; vertSpots: boolean; vertSlopes: boolean; grade: boolean; setbackDims: boolean; vertRooms: boolean; assemblyTags: boolean; vertTags: boolean; vertDims: boolean; caseworkTags: boolean; ceilingTags: boolean; lightTags: boolean; fixtureElevs: boolean; soffitSteps: boolean; ceilingHatch: boolean; ffSpots: boolean; equipTags: boolean; clearances: boolean; overhead: boolean; plDims: boolean; cornerSpots: boolean; fenceLabels: boolean; padElevs: boolean; siteLabels: boolean } {
  const recipe = viewRecipe(view)
  const base = recipe
    ? { ...recipe.annotations }
    // untyped views: all-on for the classic keys (today's exact semantics), OFF for
    // the slice-D roof package — RIDGE tags and slope arrows are roof-plan
    // documentation, not something a live story plan should sprout by default
    : { tags: true, markers: true, datums: true, grids: true, rooms: true, separators: true, site: true, stairNotes: true, roofTags: false, slopeArrows: false, roofElevs: false, drainage: false, roofHatch: false }
  // DEMOLITION PLANS (Matt, 2026-10-04: "We don't need to show doors and windows and walls
  // tags like we do on floor plans… We only need to see the plan and the demo keynotes"):
  // with the demolition phase display, the floor-plan furniture defaults OFF — door,
  // window and wall tags, room labels, separators, stair notes, parking stalls. Markers stay on (the
  // marker builder itself keeps only demolition elevations there). A stored per-view
  // value still wins, so any of these can be switched back on.
  if (view.phaseFilter === 'demo') {
    base.tags = false; base.rooms = false; base.separators = false; base.stairNotes = false
  }
  // ROOF FRAMING PLAN: the framing is the subject — the roof plan's tags, slope arrows,
  // elevations and hatch default off (each still switchable)
  const roofFraming = isRoofFramingPlan(view as Pick<ViewRecord, 'scope' | 'roofFraming'>)
  if (roofFraming) { base.roofTags = false; base.slopeArrows = false; base.roofElevs = false; base.roofHatch = false }
  const a = view.annotations
  const vertical = !!view.scope && (view.scope.kind === 'section' || view.scope.kind === 'elevation')
  // THE RCP PACKAGE (docs/bim-plan/28 round 3): a reflected ceiling plan's own annotation
  const rcp = !!view.scope && view.scope.kind === 'plan' && view.scope.planType === 'rcp'
  const planType = view.scope && view.scope.kind === 'plan' ? view.scope.planType : undefined
  const storyPlan = !!view.scope && view.scope.kind === 'plan' && !!view.scope.levelId && planType !== 'site' && planType !== 'roof'
  const sitePlan = !!view.scope && view.scope.kind === 'plan' && planType === 'site'
  return {
    // NO TAGS ON RCPs (2026-11-04, Matt: "No wall tags on RCPs, right? … turn tags off the
    // RCPs"): wall and door/window tags never draw on a reflected ceiling plan, whatever a
    // stored per-view switch says (Santiago's RCPs had it on) — the gypsum-hatch precedent
    tags: rcp ? false : (a?.tags ?? base.tags),
    markers: a?.markers ?? base.markers,
    datums: a?.datums ?? base.datums,
    grids: a?.grids ?? a?.datums ?? base.grids,
    rooms: a?.rooms ?? base.rooms,
    separators: a?.separators ?? (opts?.sheet ? false : base.separators),
    // bim-plan/14 slice C (resolved Q5): site records default ON everywhere a
    // recipe doesn't say otherwise — screened on story plans, full on site plans
    site: a?.site ?? base.site,
    // STAIR round: tread numbers + UP arrow — recipe default, per-view override
    stairNotes: a?.stairNotes ?? base.stairNotes,
    // slice D: the roof annotation package (skeleton-derived, roof recipe only)
    roofTags: a?.roofTags ?? base.roofTags,
    slopeArrows: a?.slopeArrows ?? base.slopeArrows,
    roofElevs: a?.roofElevs ?? base.roofElevs,
    // slice E: the drainage overlay — opt-in everywhere (a working diagram)
    drainage: a?.drainage ?? base.drainage,
    // ROOF HATCH round: same family as roofTags/slopeArrows/roofElevs above
    roofHatch: a?.roofHatch ?? base.roofHatch,
    // AREA PLAN (2026-09-29): the breakout shows ONLY on Area Plans — every other
    // type and the untyped live plan never draw it, whatever a stored value says;
    // on an Area Plan it can still be switched off per view
    areaBreakout: recipe?.annotations.areaBreakout ? (a?.areaBreakout ?? true) : false,
    // AUTO-DIMENSIONS (2026-09-29): Dimension Plans only, the same rule — on by
    // default there, switchable per view, never drawn on any other type
    autoDims: recipe?.annotations.autoDims ? (a?.autoDims ?? true) : false,
    // PARKING STALLS (2026-10-02): on by default on every plan, switchable per view
    // (only the live plan draws them — never a sheet or PDF, by Matt's decision)
    parkingStalls: a?.parkingStalls ?? (view.phaseFilter !== 'demo' && planType !== 'framing' && planType !== 'foundation'),
    // STORY POLES (2026-10-03): Story Pole Plans only, the area/autoDims rule
    storyPoles: recipe?.annotations.storyPoles ? (a?.storyPoles ?? true) : false,
    // STRUCTURAL PLANS (2026-10-21): Framing / Foundation Plans only, the same rule
    framing: recipe?.annotations.framing || roofFraming ? (a?.framing ?? true) : false,
    footings: recipe?.annotations.footings ? (a?.footings ?? true) : false,
    // DEMOLITION KEYNOTES (2026-10-04): a demolition plan is a Floor Plan (or RCP) with
    // the demolition phase display, not its own type — so the gate is the display
    // HEIGHT DATUM (2026-10-07): Site Plans only, OFF until switched on per view
    heightDatum: !!view.scope && view.scope.kind === 'plan' && view.scope.planType === 'site' ? (a?.heightDatum ?? false) : false,
    // HEIGHT LIMIT LINES (2026-10-07): sections and elevations, on by default
    heightLimit: !!view.scope && (view.scope.kind === 'section' || view.scope.kind === 'elevation') ? (a?.heightLimit ?? true) : false,
    // ROOF KEYNOTES (2026-10-30): the demolition / new-work roof plan too (no level, its roofs)
    demoKeynotes: view.phaseFilter === 'demo' && !!view.scope && view.scope.kind === 'plan' && (!!view.scope.levelId || planType === 'roof') ? (a?.demoKeynotes ?? true) : false,
    // CONSTRUCTION KEYNOTES (2026-10-15): every plan that shows a phase's construction
    workKeynotes: view.phaseFilter !== 'demo' && view.phaseFilter !== 'existing' && !!view.scope && view.scope.kind === 'plan' && ((!!view.scope.levelId && (!view.scope.planType || view.scope.planType === 'floor')) || (planType === 'roof' && !roofFraming)) ? (a?.workKeynotes ?? true) : false,
    // DEMOLITION TABULATION (round 11): demolition floor plans and the demolition roof plan
    demoTabs: view.phaseFilter === 'demo' && !!view.scope && view.scope.kind === 'plan' && (!!view.scope.levelId || planType === 'roof') && !view.scope.region ? (a?.demoTabs ?? true) : false,
    // EXISTING NONCONFORMING (round 11): existing / as-built plans and existing elevations
    // and sections only, OFF until switched on per view (Matt: "allow us to switch on")
    nonconforming: (view.phaseFilter === 'existing' || planType === 'asbuilt') && !!view.scope && view.scope.kind !== 'detail' ? (a?.nonconforming ?? false) : false,
    // VIEW ANNOTATION TOGGLES (2026-10-08, Matt: "include the building mask as an
    // annotation checkbox to hide. Same with plumbing tags, downspouts … building finish
    // floor/entry annotations"): all four on by default everywhere, switchable per view
    buildingMask: a?.buildingMask ?? true,
    // PRESENTATION PLAN: the finish-floor / entry marks and plumbing hookup tags are tags
    // STRUCTURAL PLANS: the architect's marks stay off the engineer's background
    finishFloor: a?.finishFloor ?? (planType !== 'presentation' && planType !== 'framing' && planType !== 'foundation'),
    // SITE PLANS (2026-10-20, Matt: "the site plan shows utility hookups like cold water lines
    // etc. inside the home. Turn those off"): off by default there too, switchable per view
    hookups: a?.hookups ?? (planType !== 'presentation' && planType !== 'site' && planType !== 'framing' && planType !== 'foundation'),
    downspouts: a?.downspouts ?? true,
    // FINISH LAYOUT (2026-10-10, Matt: "only show in the finish plan to keep the others clean"):
    // the tile / plank joints of laid floor finishes — Finish Plans only, on by default there,
    // switchable per view, never drawn on any other plan whatever a stored value says
    floorPatterns: planType === 'finish' ? (a?.floorPatterns ?? true) : false,
    // VERTICAL ANNOTATION round (docs/bim-plan/28 round 1): sections and elevations
    // only, on by default there, switchable per view — never drawn on a plan
    vertSpots: vertical ? (a?.vertSpots ?? true) : false,
    vertSlopes: vertical ? (a?.vertSlopes ?? true) : false,
    grade: vertical ? (a?.grade ?? true) : false,
    // SITE PLAN COMPLETION (round 5): a site plan's setback labels + dimensions ride the same key
    setbackDims: vertical || sitePlan ? (a?.setbackDims ?? true) : false,
    // room names at the cut — sections only (an elevation sees the outside)
    vertRooms: !!view.scope && view.scope.kind === 'section' ? (a?.vertRooms ?? true) : false,
    // ASSEMBLY TAGS (round 2): sections, roof plans and RCPs by default; any plan or
    // section can switch them; an elevation has no cut assemblies to tag
    // CASEWORK TAGS (Casework Studio stage 6): INTERIOR ELEVATIONS only (2026-10-13, Matt: "we
    // only need those to show in interior elevations" — not sections, not exterior elevations); an
    // interior elevation is an elevation tied to its storey (`scope.levelId`)
    caseworkTags: !!view.scope && view.scope.kind === 'elevation' && !!view.scope.levelId ? (a?.caseworkTags ?? true) : false,
    // THE RCP PACKAGE (round 3): ceiling heights, light tags, fixture heights and soffit
    // steps — reflected ceiling plans only, on by default there, switchable per view
    ceilingTags: rcp ? (a?.ceilingTags ?? true) : false,
    // LIGHT TAGS IN EXTERIOR ELEVATIONS (2026-10-19, Matt): the same key tags the exterior
    // lights an exterior elevation sees — on by default there, never on an interior elevation
    lightTags: rcp || (!!view.scope && view.scope.kind === 'elevation' && !view.scope.levelId) ? (a?.lightTags ?? true) : false,
    fixtureElevs: rcp ? (a?.fixtureElevs ?? true) : false,
    soffitSteps: rcp ? (a?.soffitSteps ?? true) : false,
    // GYPSUM BOARD HATCH — always on every RCP, no switch (2026-11-03, Matt: "Turn it on by
    // default. Don't provide the option"); a stored value from the day it was a switch is ignored
    ceilingHatch: rcp,
    // FLOOR PLAN COMPLETION (round 4): finish-floor spots on Floor and Dimension Plans,
    // equipment legend tags on Floor Plans — on by default there, switchable on any story plan
    ffSpots: storyPlan ? (a?.ffSpots ?? (planType === 'floor' || planType === 'dimension' || planType === 'subfloor')) : false,
    equipTags: storyPlan ? (a?.equipTags ?? planType === 'floor') : false,
    // code clearances (toilets, showers) and the overhead eaves of the roof above — Floor Plans
    clearances: storyPlan ? (a?.clearances ?? planType === 'floor') : false,
    overhead: sitePlan ? (a?.overhead ?? true) : storyPlan && planType !== 'rcp' ? (a?.overhead ?? planType === 'floor') : false,
    // SITE PLAN COMPLETION (round 5): property line to face of stud (decimal feet), the
    // surveyed grade at each property corner, fence / site wall height + top — site plans
    plDims: sitePlan ? (a?.plDims ?? true) : false,
    cornerSpots: sitePlan ? (a?.cornerSpots ?? true) : false,
    fenceLabels: sitePlan ? (a?.fenceLabels ?? true) : false,
    padElevs: sitePlan ? (a?.padElevs ?? true) : false,
    siteLabels: sitePlan ? (a?.siteLabels ?? true) : false,
    assemblyTags: !view.scope || view.scope.kind === 'elevation' ? false
      : (a?.assemblyTags ?? (view.scope.kind === 'section'
        || (view.scope.kind === 'plan' && !roofFraming && (view.scope.planType === 'roof' || view.scope.planType === 'rcp' || view.scope.planType === 'subfloor')))),
    // DOOR + WINDOW MARKS (round 1 session 2): the plan's marks on every opening a
    // section cuts or an elevation sees
    vertTags: vertical ? (a?.vertTags ?? true) : false,
    // PLATE + BEAM HEIGHTS: dimensioned from their level at a section's cut
    vertDims: !!view.scope && view.scope.kind === 'section' ? (a?.vertDims ?? true) : false,
  }
}

/** User-drafting kinds this view renders; null = all (untyped views, verticals). */
export function visibleDraftingKinds(
  view: Pick<ViewRecord, 'scope'> | null | undefined,
): Set<DraftingRecord['kind']> | null {
  const recipe = viewRecipe(view)
  if (!recipe) return null
  const out = new Set<DraftingRecord['kind']>()
  for (const k of ['dimension', 'text', 'spot', 'line', 'region'] as const) if (recipe.drafting[k]) out.add(k)
  return out
}

/**
 * STORY UNDERLAY (docs/MODELER.md "STORY UNDERLAY + PLAN VIEW DEPTH"): resolve a plan
 * view's `underlayStory` setting to an actual LevelId, or null (no underlay). Resolved
 * at READ time, never stored — 'below'/'above' re-derive against the CURRENT
 * elevation-sorted level list on every call, so inserting a level or re-benchmarking
 * the site datum can never leave a stale underlay pointed at the wrong story (the
 * same live-resolution convention as `viewRecipe` and `deriveViewCrop`'s below/above
 * math). Absent `underlayStory` reads as 'below' — the one ViewRecord field where
 * "absent" isn't "off"; 'none' turns it off explicitly. A pinned specific-story id
 * that no longer exists resolves to null (degrades gracefully, the topo/grade-point
 * convention — never throws).
 */
export function resolveUnderlayLevelId(
  model: ModelDoc,
  view: Pick<ViewRecord, 'scope' | 'underlayStory'> | null | undefined,
): LevelId | null {
  if (!view || view.scope.kind !== 'plan') return null
  const scope = view.scope
  // level-free plans (site/roof — bim-plan/14): no story of their own, so a story
  // underlay is meaningless — 'below'/'above' have nothing to be relative to, and
  // the whole-model unlimited range already shows every story's real content
  if (!scope.levelId) return null
  // UNDERLAY DEFAULT fix (2026-08-26, Matt's field pass — the misdiagnosed round:
  // "we see the entire bathroom's suite of stuff in the lower level through the
  // floor", "doors show a gray solid line under a muted red dashed line"): every
  // one of those gray reads was the STORY UNDERLAY — the screened, deliberately
  // un-occluded ghost of the level below, defaulted ON for every plan view. A
  // ghost of the whole story below is a TRACING aid, right for the live working
  // plan and wrong for every typed DOCUMENTATION view (a real floor plan doesn't
  // print the story below; in a split-level the shared tall walls cut on BOTH
  // stories, so the ghost lands exactly under the active level's own linework —
  // the "gray solid under the demo dash"). Typed plan views now default 'none';
  // the untyped live plan keeps 'below'. An explicit stored underlayStory wins in
  // both cases, and the toolbar dropdown still opts any view back in.
  // STRUCTURAL PLANS: a Framing Plan looks down at its own floor — the walls under it are the
  // story below, ghosted by default
  const setting = view.underlayStory ?? (scope.planType && scope.planType !== 'framing' ? 'none' : 'below')
  if (setting === 'none') return null
  const levels = listLevels(model).slice().sort((a, b) => a.elevationFt - b.elevationFt)
  // UNDERLAY THIS LEVEL (2026-11-04, Matt: align RCP lighting to plan features and vice
  // versa): 'self' ghosts the view's OWN story — with the orientation flipped, an RCP
  // shows its floor plan under it (Look Down) and a floor plan its ceiling (Look Up)
  if (setting === 'self') return levels.some((l) => l.id === scope.levelId) ? scope.levelId : null
  if (setting === 'below' || setting === 'above') {
    const i = levels.findIndex((l) => l.id === scope.levelId)
    if (i < 0) return null
    const neighbor = levels[setting === 'below' ? i - 1 : i + 1]
    return neighbor ? neighbor.id : null
  }
  // a pinned specific story — confirm it still exists (levels can be deleted)
  return levels.some((l) => l.id === setting) ? setting : null
}

/** REVIT UNDERLAY round: does this view's underlay look UP (RCP-style)? */
export function underlayLooksUp(view: Pick<ViewRecord, 'underlayOrientation'> | null | undefined): boolean {
  return view?.underlayOrientation === 'up'
}

/**
 * REVIT UNDERLAY round — Revit's automatic "Range: Top Level": the story right above
 * the underlay's base story (null when the base is the top story, or there is no
 * underlay). Read-only, derived — Revit sets it for you and so do we.
 */
export function underlayRangeTop(
  model: ModelDoc,
  view: Pick<ViewRecord, 'scope' | 'underlayStory'> | null | undefined,
): { id: LevelId; name: string } | null {
  const base = resolveUnderlayLevelId(model, view)
  if (!base) return null
  const levels = listLevels(model).slice().sort((a, b) => a.elevationFt - b.elevationFt)
  const i = levels.findIndex((l) => l.id === base)
  const top = i >= 0 ? levels[i + 1] : null
  return top ? { id: top.id, name: top.name } : null
}

export interface UnderlayStoryOption { value: 'none' | 'self' | 'below' | 'above' | LevelId; label: string }

/**
 * The picker's option list for a plan view's underlay select — shared by the live
 * plan card and the sheet-viewport settings so both name "Level below"/"Level above"
 * against the SAME resolved neighbor (never a bare relative label with nothing to
 * confirm it against).
 */
export function underlayStoryOptions(model: ModelDoc, currentLevelId: LevelId): UnderlayStoryOption[] {
  const levels = listLevels(model).slice().sort((a, b) => a.elevationFt - b.elevationFt)
  const i = levels.findIndex((l) => l.id === currentLevelId)
  const below = i > 0 ? levels[i - 1] : null
  const above = i >= 0 && i + 1 < levels.length ? levels[i + 1] : null
  const out: UnderlayStoryOption[] = [
    { value: 'none', label: 'None' },
    { value: 'self', label: i >= 0 ? `This level (${levels[i].name})` : 'This level' },
    { value: 'below', label: below ? `Level below (${below.name})` : 'Level below (none)' },
    { value: 'above', label: above ? `Level above (${above.name})` : 'Level above (none)' },
  ]
  for (const l of levels) if (l.id !== currentLevelId) out.push({ value: l.id, label: l.name })
  return out
}

/**
 * SITE PLAN BUILDING FOOTPRINT (2026-10-02): the ABSOLUTE cut elevation a level-free
 * plan actually cuts at — a site plan's `cutLevel` (that level's finish floor + aboveFt,
 * read live so it follows the level), else its stored `cutElevFt`, else null (derived
 * above all content). The ONE resolver every plan request (live, sheets, PDF, DXF) uses.
 */
export function effectiveCutElevFt(model: ModelDoc, view: Pick<ViewRecord, 'scope'> | null | undefined): number | null {
  const s = view?.scope
  if (!s || s.kind !== 'plan' || s.levelId) return null
  const cl = s.planType === 'site' ? s.cutLevel : null
  if (cl?.levelId) {
    const lv = getLevel(model, cl.levelId)
    if (lv) return lv.elevationFt + (typeof cl.aboveFt === 'number' ? cl.aboveFt : 4)
  }
  return s.cutElevFt ?? null
}

/**
 * ROOF PLAN VIEW DEPTH (2026-10-19): the absolute elevation a LEVEL-FREE plan's view
 * range stops at — `depthLevel`'s level finish floor + offsetFt, read live. Null = the
 * unlimited range (the default), also when the level has since been deleted. The ONE
 * resolver the live plan, its picking, sheets and the PDF all read.
 */
export function effectiveDepthElevFt(model: ModelDoc, view: Pick<ViewRecord, 'scope'> | null | undefined): number | null {
  const s = view?.scope
  if (!s || s.kind !== 'plan' || s.levelId || !s.depthLevel?.levelId) return null
  const lv = getLevel(model, s.depthLevel.levelId)
  if (!lv) return null
  return lv.elevationFt + (typeof s.depthLevel.offsetFt === 'number' ? s.depthLevel.offsetFt : 0)
}

/** SITE PLAN BUILDING FOOTPRINT: the level a site plan is cut at (null = none) */
export function siteCutLevelId(model: ModelDoc, view: Pick<ViewRecord, 'scope'> | null | undefined): LevelId | null {
  const s = view?.scope
  if (!s || s.kind !== 'plan' || s.levelId || s.planType !== 'site' || !s.cutLevel?.levelId) return null
  return getLevel(model, s.cutLevel.levelId) ? s.cutLevel.levelId : null
}
